import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { Cortador, energiaDe, type Corte } from './cortador';
import { chaveDe, fila, type TrechoNaFila } from './fila';
import { montarWav, paraInt16, reamostrar, TAXA_STT } from './wav';

/** Pedaço da tomada contínua, para remontar o áudio no fim. */
const PARTE_MS = 30_000;

export interface EstadoDoGravador {
  gravando: boolean;
  duracaoMs: number;
  nivel: number;
  pendentes: number;
  erro: string;
}

type Ouvinte = (estado: EstadoDoGravador) => void;

export interface OpcoesDoGravador {
  /**
   * Desliga a tomada contínua — a trilha que existe só para o player tocar o
   * áudio depois.
   *
   * O ditado no compositor não guarda áudio: quer o texto e mais nada. Com a
   * tomada ligada, cada frase ditada vira uma parte, e uma parte faz o
   * `finalizar` enfileirar NORMALIZAR, que roda ffmpeg — concatenar, sondar,
   * gerar `stt.flac` e `reproducao.m4a`, e subir dois objetos. Numa VPS com
   * disco em 82% que hospeda produção de terceiros, pagar isso por uma frase
   * de cinco segundos seria desperdício em cima de desperdício.
   */
  semTomadaContinua?: boolean;
}

interface UrlAssinada {
  ordem: number;
  url: string;
}

/**
 * Grava do microfone, corta em pedaços e vai enviando enquanto a pessoa fala.
 *
 * Duas trilhas em paralelo, de propósito: pedaços curtos em WAV 16 kHz para
 * transcrever na hora, e a tomada contínua em partes de 30 s para remontar o
 * áudio depois. A primeira precisa ser pequena e rápida; a segunda precisa ser
 * fiel. Tentar usar a mesma para as duas coisas estraga uma delas.
 */
export class Gravador {
  private contexto?: AudioContext;
  private fonte?: MediaStreamAudioSourceNode;
  private processador?: ScriptProcessorNode;
  private trilha?: MediaStream;
  private mediaRecorder?: MediaRecorder;

  private cortador = new Cortador();
  private acumulado: Float32Array[] = [];
  private amostrasNoTrecho = 0;
  private amostrasTotais = 0;
  private ordemTrecho = 0;
  private ordemParte = 0;
  private gravacaoId = '';
  private enviando = false;

  private estado: EstadoDoGravador = {
    gravando: false,
    duracaoMs: 0,
    nivel: 0,
    pendentes: 0,
    erro: '',
  };

  constructor(
    private readonly aoMudar: Ouvinte,
    private readonly opcoes: OpcoesDoGravador = {},
  ) {}

  private avisar(mudanca: Partial<EstadoDoGravador>): void {
    this.estado = { ...this.estado, ...mudanca };
    this.aoMudar(this.estado);
  }

  async iniciar(titulo: string): Promise<string> {
    const criada = await chamar<{ gravacaoId: string }>(`${ROTAS.gravacoes}/microfone`, {
      method: 'POST',
      corpo: { titulo },
    });
    this.gravacaoId = criada.gravacaoId;

    this.trilha = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });

    // Contexto na taxa nativa: reamostrar aqui dá mais controle do que pedir
    // 16 kHz ao navegador, que nem todos respeitam.
    this.contexto = new AudioContext();
    this.fonte = this.contexto.createMediaStreamSource(this.trilha);
    this.processador = this.contexto.createScriptProcessor(4096, 1, 1);

    this.processador.onaudioprocess = (evento) => {
      const entrada = evento.inputBuffer.getChannelData(0);
      this.aoReceberAudio(new Float32Array(entrada), this.contexto?.sampleRate ?? 48_000);
    };
    this.fonte.connect(this.processador);
    // Ligado ao destino com volume zero: sem isto o ScriptProcessor não roda.
    const mudo = this.contexto.createGain();
    mudo.gain.value = 0;
    this.processador.connect(mudo);
    mudo.connect(this.contexto.destination);

    if (!this.opcoes.semTomadaContinua) this.iniciarTomadaContinua();
    this.avisar({ gravando: true, erro: '' });
    return this.gravacaoId;
  }

  /** Tomada contínua em partes: é o áudio que o player vai tocar depois. */
  private iniciarTomadaContinua(): void {
    if (!this.trilha) return;
    const tipos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    const suportado = tipos.find((t) => MediaRecorder.isTypeSupported(t));
    if (!suportado) return;

    this.mediaRecorder = new MediaRecorder(this.trilha, { mimeType: suportado });
    this.mediaRecorder.ondataavailable = (evento) => {
      if (evento.data.size === 0) return;
      const ordem = this.ordemParte;
      this.ordemParte += 1;
      void this.enfileirar({
        chave: chaveDe(this.gravacaoId, 'parte', ordem),
        gravacaoId: this.gravacaoId,
        ordem,
        inicioMs: ordem * PARTE_MS,
        fimMs: (ordem + 1) * PARTE_MS,
        blob: evento.data,
        tipo: 'parte',
      });
    };
    this.mediaRecorder.start(PARTE_MS);
  }

  private aoReceberAudio(bloco: Float32Array, taxa: number): void {
    const emStt = reamostrar(bloco, taxa);
    this.acumulado.push(emStt);
    this.amostrasNoTrecho += emStt.length;
    this.amostrasTotais += emStt.length;

    const fimMs = Math.round((this.amostrasTotais / TAXA_STT) * 1000);
    const nivel = energiaDe(emStt);
    this.avisar({ duracaoMs: fimMs, nivel });

    const corte = this.cortador.aoQuadro({ energia: nivel, fimMs });
    if (corte) this.fecharTrecho(corte);
  }

  private fecharTrecho(corte: Corte): void {
    const amostras = new Float32Array(this.amostrasNoTrecho);
    let posicao = 0;
    for (const bloco of this.acumulado) {
      amostras.set(bloco, posicao);
      posicao += bloco.length;
    }
    this.acumulado = [];
    this.amostrasNoTrecho = 0;

    const ordem = this.ordemTrecho;
    this.ordemTrecho += 1;
    void this.enfileirar({
      chave: chaveDe(this.gravacaoId, 'trecho', ordem),
      gravacaoId: this.gravacaoId,
      ordem,
      inicioMs: corte.inicioMs,
      fimMs: corte.fimMs,
      blob: montarWav(paraInt16(amostras)),
      tipo: 'trecho',
    });
  }

  private async enfileirar(item: TrechoNaFila): Promise<void> {
    try {
      await fila.guardar(item);
      this.avisar({ pendentes: (await fila.pendentes(this.gravacaoId)).length });
      void this.escoar();
    } catch (erro) {
      this.avisar({ erro: 'nao consegui guardar o audio: ' + (erro as Error).message });
    }
  }

  /**
   * Envia o que estiver na fila, um de cada vez e sempre pedindo a URL na
   * hora. O que falhar fica para a próxima rodada — nada é descartado.
   */
  private async escoar(): Promise<void> {
    if (this.enviando || !this.gravacaoId) return;
    this.enviando = true;
    try {
      for (;;) {
        const pendentes = await fila.pendentes(this.gravacaoId);
        const item = pendentes[0];
        if (!item) break;

        const caminho = item.tipo === 'trecho' ? 'trechos' : 'partes';
        const { urls } = await chamar<{ urls: UrlAssinada[] }>(
          `${ROTAS.gravacoes}/${this.gravacaoId}/${caminho}/urls`,
          { method: 'POST', corpo: { ordens: [item.ordem] } },
        );
        const destino = urls[0];
        // Sem URL: o servidor já considera este pedaço resolvido.
        if (!destino) {
          await fila.remover(item.chave);
          continue;
        }

        const envio = await fetch(destino.url, {
          method: 'PUT',
          headers: { 'content-type': item.blob.type || 'application/octet-stream' },
          body: item.blob,
        });
        if (!envio.ok) throw new Error('falha ao enviar o audio');

        await chamar(`${ROTAS.gravacoes}/${this.gravacaoId}/${caminho}/confirmar`, {
          method: 'POST',
          corpo:
            item.tipo === 'trecho'
              ? {
                  ordem: item.ordem,
                  inicioMs: item.inicioMs,
                  fimMs: item.fimMs,
                  tamanho: item.blob.size,
                }
              : { ordem: item.ordem, tamanho: item.blob.size },
        });

        await fila.remover(item.chave);
        this.avisar({
          pendentes: (await fila.pendentes(this.gravacaoId)).length,
          erro: '',
        });
      }
    } catch (erro) {
      // A rede cai, o trecho fica: a próxima tentativa pega de onde parou.
      this.avisar({ erro: 'sem conexão para enviar; o áudio está guardado aqui.' });
      void (erro as Error).message;
    } finally {
      this.enviando = false;
    }
  }

  async parar(): Promise<void> {
    const fimMs = Math.round((this.amostrasTotais / TAXA_STT) * 1000);
    const ultimo = this.cortador.encerrar(fimMs);
    if (ultimo) this.fecharTrecho(ultimo);

    this.mediaRecorder?.stop();
    this.processador?.disconnect();
    this.fonte?.disconnect();
    this.trilha?.getTracks().forEach((t) => t.stop());
    await this.contexto?.close();
    this.avisar({ gravando: false, nivel: 0 });

    // Espera a fila esvaziar antes de encerrar: finalizar com pedaço no
    // caminho deixaria a gravação sem parte do áudio.
    for (let tentativa = 0; tentativa < 30; tentativa += 1) {
      await this.escoar();
      if ((await fila.pendentes(this.gravacaoId)).length === 0) break;
      await new Promise((r) => setTimeout(r, 1_000));
    }

    await chamar(`${ROTAS.gravacoes}/${this.gravacaoId}/finalizar`, { method: 'POST' });
  }
}
